import { useState, useEffect } from 'react';
import { aquaApi } from '../aquaApi';
import { fmtDate } from '../dateUtils';

const fmt = (v) => v != null && v !== '' ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
const fmtShort = (v) => {
  const n = parseFloat(v || 0);
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(2) + 'Cr';
  if (n >= 100000)   return '₹' + (n / 100000).toFixed(2) + 'L';
  if (n >= 1000)     return '₹' + (n / 1000).toFixed(1) + 'K';
  return '₹' + n.toFixed(0);
};

const STATUS_STYLE = {
  ACTIVE:    { bg: '#e6f4ea', color: '#137333', dot: '#34a853' },
  HARVESTED: { bg: '#e8f0fe', color: '#1a73e8', dot: '#1a73e8' },
  CLOSED:    { bg: '#fce8e6', color: '#c5221f', dot: '#c5221f' },
};

export default function AquaDashboard({ crop, onNavigate }) {
  // All-crops investment state
  const [allCrops, setAllCrops]         = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [filter, setFilter]             = useState('ALL');

  // Interest state
  const [interestRows, setInterestRows] = useState([]);

  // Load all-crops summary on mount
  const loadSummary = () => {
    setSummaryLoading(true);
    Promise.all([
      aquaApi.getAllCropsSummary(),
      aquaApi.getPurchaseInterest(),
    ]).then(([crops, interest]) => {
      setAllCrops(Array.isArray(crops) ? crops : []);
      setInterestRows(Array.isArray(interest) ? interest : []);
    }).finally(() => setSummaryLoading(false));
  };
  useEffect(() => { loadSummary(); }, []);

  const shown = filter === 'ALL' ? allCrops : allCrops.filter(c => c.status === filter);

  const grandSeed      = allCrops.reduce((s, c) => s + parseFloat(c.seed_cost || 0), 0);
  const grandExp       = allCrops.reduce((s, c) => s + parseFloat(c.total_expenses || 0), 0);
  const grandChem      = allCrops.reduce((s, c) => s + parseFloat(c.total_chem || 0), 0);
  const grandFeedCost  = allCrops.reduce((s, c) => s + parseFloat(c.total_feed_cost || 0), 0);
  const grandEquipCost = allCrops.reduce((s, c) => s + parseFloat(c.total_equipment_cost || 0), 0);
  const grandCrops     = allCrops.reduce((s, c) => s + parseFloat(c.total_investment || 0), 0);
  const totalInterest  = interestRows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  const grandTotal     = grandCrops + totalInterest;

  const daysSinceStart = crop?.start_date
    ? Math.floor((new Date() - new Date(crop.start_date)) / 86400000)
    : null;

  return (
    <div>

      {/* ── Crop header banner ── */}
      {crop && (
        <div className="card" style={{ marginBottom: 16, background: 'linear-gradient(135deg,#1a73e8,#0d47a1)', color: '#fff', border: 'none' }}>
          <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '.5px' }}>🦐 {crop.crop_code}</div>
              <div style={{ opacity: .8, marginTop: 4 }}>
                {crop.total_acres} Acres · {crop.num_ponds} Ponds
                {crop.seed_count ? ` · ${Number(crop.seed_count).toLocaleString('en-IN')} Seeds` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, opacity: .7 }}>Started {fmtDate(crop.start_date)}</div>
              {daysSinceStart != null && <div style={{ fontSize: 22, fontWeight: 700 }}>DOC {daysSinceStart}</div>}
              <span style={{ background: crop.status === 'ACTIVE' ? '#34a853' : '#fbbc04', color: '#fff', padding: '2px 12px', borderRadius: 12, fontSize: 13, fontWeight: 700 }}>
                {crop.status}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── All-crops investment summary ── */}
      {!crop && (
        <div style={{ textAlign: 'center', padding: '20px 0 8px' }}>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>No crop selected — showing investment summary for all crops</div>
          <button className="btn btn-primary" style={{ padding: '5px 18px', fontSize: 13 }} onClick={() => onNavigate('crops')}>
            Open Crops →
          </button>
          <div style={{ margin: '20px 0 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, height: 1, background: '#e8eaed' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#5f6368', whiteSpace: 'nowrap' }}>📊 ALL CROPS INVESTMENT</span>
            <div style={{ flex: 1, height: 1, background: '#e8eaed' }} />
          </div>
        </div>
      )}

      {summaryLoading ? <div className="loading-msg">Loading investment summary…</div> : (
        <>
          {/* Grand total stats */}
          <div className="stats-grid aq-stats" style={{ marginBottom: 16 }}>
            <div className="stat-card" style={{
              background: 'linear-gradient(135deg,#1a73e8,#0d47a1)', color: '#fff', border: 'none',
            }}>
              <div className="stat-icon" style={{ background: 'rgba(255,255,255,.2)', fontSize: 24, flexShrink: 0 }}>💰</div>
              <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <div className="stat-label" style={{ color: 'rgba(255,255,255,.8)' }}>Grand Total Investment</div>
                <div style={{ fontWeight: 800, lineHeight: 1.1, color: '#fff', fontSize: 22, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                  {fmtShort(grandTotal)}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.75)', marginTop: 2, wordBreak: 'break-word' }}>
                  ₹{fmt(grandTotal)}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', marginTop: 1 }}>
                  {allCrops.length} crops{totalInterest > 0 ? ` + ₹${fmt(totalInterest)} interest` : ''}
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon green">🦐</div>
              <div className="stat-text">
                <div className="stat-label">Total Seed Cost</div>
                <div className="stat-value">{fmtShort(grandSeed)}</div>
                <div className="stat-sub">₹{fmt(grandSeed)}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon green">🌾</div>
              <div className="stat-text">
                <div className="stat-label">Total Feed Cost</div>
                <div className="stat-value">{fmtShort(grandFeedCost)}</div>
                <div className="stat-sub">₹{fmt(grandFeedCost)}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon blue">🧪</div>
              <div className="stat-text">
                <div className="stat-label">Total Chemicals</div>
                <div className="stat-value">{fmtShort(grandChem)}</div>
                <div className="stat-sub">₹{fmt(grandChem)}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon orange">🧾</div>
              <div className="stat-text">
                <div className="stat-label">Total Expenses</div>
                <div className="stat-value">{fmtShort(grandExp)}</div>
                <div className="stat-sub">₹{fmt(grandExp)}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon orange">🔧</div>
              <div className="stat-text">
                <div className="stat-label">Total Equipment Cost</div>
                <div className="stat-value">{fmtShort(grandEquipCost)}</div>
                <div className="stat-sub">₹{fmt(grandEquipCost)}</div>
              </div>
            </div>
            {totalInterest > 0 && (
              <div className="stat-card">
                <div className="stat-icon red">💹</div>
                <div className="stat-text">
                  <div className="stat-label">Interest Charges</div>
                  <div className="stat-value">{fmtShort(totalInterest)}</div>
                  <div className="stat-sub">₹{fmt(totalInterest)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Filter + refresh */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            {['ALL', 'ACTIVE', 'HARVESTED', 'CLOSED'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                style={{ padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                  background: filter === f ? '#1a73e8' : '#f1f3f4',
                  color: filter === f ? '#fff' : '#5f6368' }}>
                {f}
              </button>
            ))}
            <button onClick={loadSummary} style={{ marginLeft: 'auto', padding: '5px 14px', borderRadius: 20, border: '1px solid #e8eaed', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
              ↻ Refresh
            </button>
          </div>

          {/* Per-crop cards */}
          {shown.length === 0 && (
            <div className="card"><div className="card-body" style={{ textAlign: 'center', color: '#888', padding: 48 }}>No crop data found</div></div>
          )}
          <div className="aq-crop-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 16 }}>
            {shown.map(c => {
              const sc   = parseFloat(c.seed_cost || 0);
              const exp  = parseFloat(c.total_expenses || 0);
              const ch   = parseFloat(c.total_chem || 0);
              const fc   = parseFloat(c.total_feed_cost || 0);
              const ec   = parseFloat(c.total_equipment_cost || 0);
              const tot  = parseFloat(c.total_investment || 0);
              const st   = STATUS_STYLE[c.status] || STATUS_STYLE.CLOSED;
              const barSeed  = tot > 0 ? (sc / tot) * 100 : 0;
              const barExp   = tot > 0 ? (exp / tot) * 100 : 0;
              const barChem  = tot > 0 ? (ch / tot) * 100 : 0;
              const barFeed  = tot > 0 ? (fc / tot) * 100 : 0;
              const barEquip = tot > 0 ? (ec / tot) * 100 : 0;
              const doc = c.start_date ? Math.floor((new Date() - new Date(c.start_date)) / 86400000) : null;
              return (
                <div key={c.crop_id} className="card" style={{ borderTop: `3px solid ${st.dot}` }}>
                  <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontSize: 17, fontWeight: 800, color: '#202124' }}>🦐 {c.crop_code}</div>
                      <span style={{ background: st.bg, color: st.color, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{c.status}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      {c.total_acres} Acres · {c.num_ponds} Ponds
                      {c.start_date && ` · Started ${fmtDate(c.start_date)}`}
                      {c.status === 'ACTIVE' && doc != null && (
                        <span style={{ marginLeft: 8, background: '#e8f0fe', color: '#1a73e8', borderRadius: 8, padding: '1px 7px', fontWeight: 700 }}>DOC {doc}</span>
                      )}
                    </div>
                  </div>

                  <div className="aq-5col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 0, borderBottom: '1px solid #f0f0f0' }}>
                    {[
                      { label: 'Seed',      value: sc,  color: '#137333', bg: '#e6f4ea' },
                      { label: 'Expenses',  value: exp, color: '#e37400', bg: '#fef3cd' },
                      { label: 'Chemicals', value: ch,  color: '#7b1fa2', bg: '#f3e8fd' },
                      { label: 'Feed',      value: fc,  color: '#0d47a1', bg: '#e8f0fe' },
                      { label: 'Equipment', value: ec,  color: '#b45309', bg: '#fef9c3' },
                    ].map(item => (
                      <div key={item.label} style={{ padding: '10px 8px 8px', borderRight: '1px solid #f0f0f0' }}>
                        <div style={{ fontSize: 10, color: '#888', fontWeight: 600, marginBottom: 4 }}>{item.label}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: item.color }}>{fmtShort(item.value)}</div>
                        <div style={{ fontSize: 10, color: '#aaa' }}>₹{fmt(item.value)}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ fontSize: 13, color: '#555' }}>
                      {c.seed_count > 0 && (
                        <span style={{ color: '#888', fontSize: 12 }}>
                          🦐 {Number(c.seed_count).toLocaleString('en-IN')} × ₹{c.seed_rate}
                        </span>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#888' }}>TOTAL</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#c5221f' }}>{fmtShort(tot)}</div>
                    </div>
                  </div>

                  {tot > 0 && (
                    <div style={{ padding: '8px 16px 4px' }}>
                      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: '#f1f3f4' }}>
                        {barSeed  > 0 && <div style={{ width: `${barSeed}%`,  background: '#34a853' }} title={`Seed: ₹${fmt(sc)}`} />}
                        {barExp   > 0 && <div style={{ width: `${barExp}%`,   background: '#fbbc04' }} title={`Expenses: ₹${fmt(exp)}`} />}
                        {barChem  > 0 && <div style={{ width: `${barChem}%`,  background: '#9c27b0' }} title={`Chemicals: ₹${fmt(ch)}`} />}
                        {barFeed  > 0 && <div style={{ width: `${barFeed}%`,  background: '#1a73e8' }} title={`Feed: ₹${fmt(fc)}`} />}
                        {barEquip > 0 && <div style={{ width: `${barEquip}%`, background: '#e37400' }} title={`Equipment: ₹${fmt(ec)}`} />}
                      </div>
                      <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 10, color: '#888', flexWrap: 'wrap' }}>
                        <span><span style={{ color: '#34a853' }}>■</span> Seed {barSeed.toFixed(0)}%</span>
                        <span><span style={{ color: '#fbbc04' }}>■</span> Exp {barExp.toFixed(0)}%</span>
                        <span><span style={{ color: '#9c27b0' }}>■</span> Chem {barChem.toFixed(0)}%</span>
                        <span><span style={{ color: '#1a73e8' }}>■</span> Feed {barFeed.toFixed(0)}%</span>
                        <span><span style={{ color: '#e37400' }}>■</span> Equip {barEquip.toFixed(0)}%</span>
                      </div>
                    </div>
                  )}

                  <div style={{ padding: '8px 16px 12px', display: 'flex', gap: 8 }}>
                    <button className="btn-sm btn-primary"   style={{ flex: 1 }} onClick={() => onNavigate('expenses')}>Expenses</button>
                    <button className="btn-sm btn-secondary" style={{ flex: 1 }} onClick={() => onNavigate('chem')}>Chemicals</button>
                    <button className="btn-sm btn-secondary" style={{ flex: 1 }} onClick={() => onNavigate('feed')}>Feed</button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Interest Charges panel */}
          {interestRows.length > 0 && (
            <div className="card" style={{ marginTop: 24 }}>
              <div className="card-header">
                <div className="card-title">💹 Interest Charges</div>
                <div style={{ fontSize: 14, color: '#c5221f', fontWeight: 700 }}>Total: ₹{fmt(totalInterest)}</div>
              </div>
              <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Vendor / Supplier</th>
                      <th style={{ textAlign: 'right' }}>Interest (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interestRows.map(r => (
                      <tr key={r.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.interest_date)}</td>
                        <td style={{ fontWeight: 600 }}>{r.vendor_name || '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#c5221f' }}>₹{fmt(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>TOTAL INTEREST</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#c5221f' }}>₹{fmt(totalInterest)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* All-crops table */}
          <div className="card" style={{ marginTop: 24 }}>
            <div className="card-header"><div className="card-title">📋 Investment Table — All Crops</div></div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Crop Code</th><th>Status</th><th>Start Date</th><th>Acres</th>
                    <th style={{ textAlign: 'right' }}>Seed (₹)</th>
                    <th style={{ textAlign: 'right' }}>Expenses (₹)</th>
                    <th style={{ textAlign: 'right' }}>Chemicals (₹)</th>
                    <th style={{ textAlign: 'right' }}>Feed Cost (₹)</th>
                    <th style={{ textAlign: 'right' }}>Equipment (₹)</th>
                    <th style={{ textAlign: 'right' }}>Total Investment (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {allCrops.length === 0 && <tr><td colSpan={10} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No crop data</td></tr>}
                  {allCrops.map(c => {
                    const st = STATUS_STYLE[c.status] || STATUS_STYLE.CLOSED;
                    return (
                      <tr key={c.crop_id}>
                        <td><strong>{c.crop_code}</strong></td>
                        <td><span style={{ background: st.bg, color: st.color, borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>{c.status}</span></td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(c.start_date)}</td>
                        <td style={{ textAlign: 'right' }}>{c.total_acres}</td>
                        <td style={{ textAlign: 'right', color: '#137333', fontWeight: 600 }}>₹{fmt(c.seed_cost)}</td>
                        <td style={{ textAlign: 'right' }}>₹{fmt(c.total_expenses)}</td>
                        <td style={{ textAlign: 'right' }}>₹{fmt(c.total_chem)}</td>
                        <td style={{ textAlign: 'right', color: '#0d47a1' }}>₹{fmt(c.total_feed_cost)}</td>
                        <td style={{ textAlign: 'right', color: '#b45309' }}>₹{fmt(c.total_equipment_cost)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#c5221f' }}>₹{fmt(c.total_investment)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {allCrops.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ fontWeight: 700, padding: '10px 12px' }}>CROPS TOTAL</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#137333' }}>₹{fmt(grandSeed)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>₹{fmt(grandExp)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>₹{fmt(grandChem)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#0d47a1' }}>₹{fmt(grandFeedCost)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#b45309' }}>₹{fmt(grandEquipCost)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#c5221f' }}>₹{fmt(grandCrops)}</td>
                    </tr>
                    {totalInterest > 0 && (
                      <tr style={{ background: '#fff8f8' }}>
                        <td colSpan={9} style={{ textAlign: 'right', fontWeight: 600, padding: '8px 12px', color: '#c5221f', fontSize: 13 }}>+ Interest Charges</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, padding: '8px 12px', color: '#c5221f' }}>₹{fmt(totalInterest)}</td>
                      </tr>
                    )}
                    {totalInterest > 0 && (
                      <tr style={{ background: 'linear-gradient(135deg,#fff3f0,#fce8e6)', borderTop: '2px solid #c5221f' }}>
                        <td colSpan={9} style={{ textAlign: 'right', fontWeight: 800, padding: '10px 12px', fontSize: 14 }}>GRAND TOTAL (incl. Interest)</td>
                        <td style={{ textAlign: 'right', fontWeight: 800, padding: '10px 12px', color: '#c5221f', fontSize: 15 }}>₹{fmt(grandTotal)}</td>
                      </tr>
                    )}
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
